import React from 'react';
import { StyleSheet, Image, View, StyleProp, ViewStyle } from 'react-native';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import Stack, { contentStackSpacing } from '../Stack';
import Text from '../Text';
import { shadows } from '../../../utils/theme';
import Icon, { IconName } from '../Icon';
import NotificationCountIndicator, {
    NOTIFICATION_COUNT_INDICATOR_SIZE,
} from '../NotificationIndicator/NotificationCountIndicator';

export const CARD_BUTTON_SIZE = 108;

const styles = StyleSheet.create({
    root: {
        alignItems: 'center',
        borderRadius: 5,
        height: CARD_BUTTON_SIZE,
        justifyContent: 'center',
        width: CARD_BUTTON_SIZE,
        backgroundColor: 'white',
    },
});

export type CardButtonOwnProps = {
    title: string;
    onPress: () => void;
    isDisabled?: boolean;
    subText?: string | null;
    testID?: string;
    cardStyle?: StyleProp<ViewStyle>;
    style?: StyleProp<ViewStyle>;
    hoverContainerStyles?: StyleProp<ViewStyle>;
    notificationCount?: number;
} & ({ image: string } | { icon: IconName });

export type CardButtonProps = CardButtonOwnProps;

const iconSize = 48;

const CardButton: React.FC<CardButtonProps> = (props) => {
    const maybeWrapImageForNotificationCountIndicator = (children: React.ReactNode) => {
        if (!props.notificationCount) return children;

        const container = (children: React.ReactNode) => (
            <View
                style={{ width: NOTIFICATION_COUNT_INDICATOR_SIZE, alignSelf: 'stretch', justifyContent: 'flex-start' }}
            >
                {children}
            </View>
        );

        return (
            <Stack
                horizontal
                style={{ alignSelf: 'stretch', paddingLeft: 8, paddingRight: 8 }}
                justify="space-between"
                testID="notification-count-indicator-container"
            >
                {container(null)}
                {children}
                {container(<NotificationCountIndicator count={props.notificationCount} />)}
            </Stack>
        );
    };

    return (
        <PressableDynamicOpacity
            isDisabled={props.isDisabled}
            onPress={props.onPress}
            testID={props.isDisabled ? `${props.testID}-disabled` : props.testID}
            addHoverEffect
            style={props.style}
            hoverDetectionContainerStyles={props.hoverContainerStyles}
        >
            <Stack
                vertical
                justify={'space-around'}
                style={[styles.root, shadows.card, props.cardStyle]}
                space={contentStackSpacing}
            >
                {maybeWrapImageForNotificationCountIndicator(
                    'image' in props ? (
                        <View style={{ width: iconSize, height: iconSize }}>
                            <Image
                                source={{ uri: props.image }}
                                defaultSource={{ uri: props.image }}
                                style={{
                                    height: '100%',
                                    width: '100%',
                                }}
                                resizeMode="contain"
                            />
                        </View>
                    ) : (
                        <Icon name={props.icon} size="card-button-icon" color="Primary" />
                    ),
                )}
                <Stack vertical align="center">
                    <Text variant="button">{props.title}</Text>
                    <Text variant="card-support">{props.subText || ' '}</Text>
                </Stack>
            </Stack>
        </PressableDynamicOpacity>
    );
};

export default React.memo(CardButton);

export const SpacerCardButton: React.FC = () => (
    <View style={{ width: CARD_BUTTON_SIZE, height: CARD_BUTTON_SIZE, backgroundColor: 'transparent' }} />
);
